import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchBrandProfile } from "../../brand/services/brandService";

export type BrandSetUpStatus = "loading" | "ready" | "failed";

export interface BrandSetUpState {
  /** Whether a brand has been saved. `false` until the answer is in. */
  brandSetUp: boolean;
  status: BrandSetUpStatus;
}

/**
 * Whether the workspace has set its brand up — the first tick on the
 * dashboard's setup checklist.
 *
 * A second call on top of the dashboard's own, because the summary endpoint
 * says nothing about the brand. It is the same small `GET /api/v1/brand/` the
 * My brand screen opens with, and it is read for one bit: a workspace that has
 * never saved a brand gets `null` back, which is what an untouched step looks
 * like.
 *
 * Read under `["brand", "profile"]` — its own key, separate from
 * `useBrandDetails`'s `["brand", "details"]`, which merges this same profile
 * with the payment record into one form-shaped object; this hook wants the
 * profile alone. Backed by TanStack Query all the same, so the checklist does
 * not sit on a fresh request every time the dashboard opens.
 *
 * Quiet on failure, and that is deliberate: nobody came to this screen to look
 * at their brand, so a toast about it would be noise on top of whatever the
 * dashboard's own load is already saying. The caller drops the checklist
 * instead — a tick nobody can vouch for is worse than no checklist at all.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useBrandSetUp(): BrandSetUpState {
  const getToken = useTokenGetter();

  const query = useQuery({
    // Not `BRAND_DETAILS_QUERY_KEY`: that key holds the merged form object
    // `useBrandDetails` builds, not the raw profile this reads.
    queryKey: ["brand", "profile"] as const,
    queryFn: ({ signal }) => fetchBrandProfile(getToken, signal),
  });

  return {
    // The name is what makes a brand a brand: the record can exist with every
    // other field blank, and a step ticked off that is a lie.
    brandSetUp: Boolean(query.data?.brandName?.trim()),
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
  };
}

export default useBrandSetUp;
